import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {EliteformTableOfContentsService} from '@shared/components/platform-tool/platform-tool-dialog-definition/services/eliteform-table-of-contents.service';
import {Subject} from 'rxjs';
import {EliteFormQuestionResponse} from '@features/elite-form/components/elite-form-table-of-contents/elite-form-table-of-contents-container/elite-form-table-of-contents-container.component';
import {takeUntil} from 'rxjs/operators';

@Component({
    standalone: false,
    selector: 'app-elite-form-table-of-contents-item',
    templateUrl: './elite-form-table-of-contents-item.component.html',
    styleUrl: './elite-form-table-of-contents-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EliteFormTableOfContentsItemComponent implements OnInit {
    @Input() questions: EliteFormQuestionResponse[] = [];
    @Input() parentIndex: string = '';
    @Input() level = 0;
    @Input() currentField;
    private ngUnsubscribe$ = new Subject<void>();
    selectedFieldKey: string;
    errorCountToolTip: string = 'Validation warnings';

    trackByQuestionKey = (_: number, q: EliteFormQuestionResponse) =>
        (q?.fieldKey ?? '') + '--' + (q?.dataItemId ?? '');


    constructor(
        private tocService: EliteformTableOfContentsService,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        this.tocService.selectedFieldKey$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(key => {
                this.selectedFieldKey = key;
                this.cdr.markForCheck();
            });
    }

    scrollToField(fieldKey: string, pageIndex: string, dataItemId: string) {
        this.selectedFieldKey = fieldKey + '--' + dataItemId;
        this.tocService.selectField(this.selectedFieldKey);

        this.tocService.goToPage(this.getFirstNumber(pageIndex) - 1);
        this.tocService.scrollTo(this.selectedFieldKey);
    }

    private getFirstNumber(part: string): number {
        return parseInt(part.split('.')[0], 10);
    }

    onSectionExpanded(dataItem: EliteFormQuestionResponse) {
        this.tocService.expandSection(dataItem.dataItemId);
    }

    get filteredQuestions() {
        return (this.questions || [])
            .slice()
            .filter(q => q != null)
            .filter(q => q.dataType !== 'Title' && q.dataType !== 'Description')
            .sort((a, b) => {
                if (a.row !== b.row) {
                    return a.row - b.row;  // primary sort by row
                }
                return a.column - b.column;  // secondary sort by column
            }) ?? [];
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    getErrorCount(question: EliteFormQuestionResponse) {
        return question.errorInfo.totalErrorCount;
    }

    getErrorMessage(question: EliteFormQuestionResponse) {
        return question.errorInfo.errorMessages[0];
    }

    getDisplayTitle(question: EliteFormQuestionResponse) {
        return question.title?.trim() || question.description.trim() || ' ';
    }

    getListStyle(question: any, depth?: any) {
        const currentDepth = depth ? depth : this.level;
        let liStyle = {};
        liStyle = this.getSelectedFieldStyle(question);
        if (question.dataType !== 'Group') {
            liStyle['padding-left'] = `${currentDepth * 20}px`;
        }
        if (question && question.dataType !== 'Group') {
            liStyle['border-bottom'] = '1px solid #DEE4EA!important';
        }
        if (question.isMultiResponse && depth) {
            liStyle['margin-inline'] = '0';
        }
        return liStyle;
    }

    getMatHeaderStyle(question, depth?: any) {
        const currentDepth = depth ? depth : this.level;
        return {
            'padding-left': `${currentDepth * 20}px`,
            'border-bottom': '1px solid #DEE4EA!important'
        };
    }

    getSelectedFieldStyle(question) {
        const fieldId = question.fieldKey + '--' + question.dataItemId;
        let ret = this.selectedFieldKey === fieldId;
        if (ret) {
            return {
                backgroundColor: '#EFF6FF',
                color: '#175CFF',
                boxShadow: 'inset 3px 0 0 0 #175CFF',
            };
        } else {
            return {};
        }
    }
}
